import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Check, X, ArrowRight, Sparkles, Zap, Shield, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export function ProductShowcase() {
  const { t } = useTranslation();
  const painPoints = [
    {
      pain: t('landing.productShowcase.painPoints.pain1'),
      solution: t('landing.productShowcase.painPoints.solution1'),
    },
    {
      pain: t('landing.productShowcase.painPoints.pain2'),
      solution: t('landing.productShowcase.painPoints.solution2'),
    },
    {
      pain: t('landing.productShowcase.painPoints.pain3'),
      solution: t('landing.productShowcase.painPoints.solution3'),
    },
    {
      pain: t('landing.productShowcase.painPoints.pain4'),
      solution: t('landing.productShowcase.painPoints.solution4'),
    },
  ];

  const floatingIcons = [
    { Icon: Sparkles, color: 'text-yellow-500', delay: 0 },
    { Icon: Zap, color: 'text-emerald-500', delay: 0.2 },
    { Icon: Shield, color: 'text-blue-500', delay: 0.4 },
    { Icon: Globe, color: 'text-purple-500', delay: 0.6 }
  ];

  return (
    <section className="py-20 md:py-32 relative overflow-hidden">
      {/* OpenClaw Logo Background */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      >
        <motion.img
          src="/openclaw.svg"
          alt=""
          className="w-[55%] h-auto opacity-[0.015] dark:opacity-[0.025]"
          animate={{ rotate: 360 }}
          transition={{ duration: 120, repeat: Infinity, ease: 'linear' }}
        />
      </motion.div>

      {/* Animated Grid Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-emerald-500/5 to-background" />
        <motion.div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgb(16 185 129 / 0.1) 1px, transparent 1px),
              linear-gradient(to bottom, rgb(16 185 129 / 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
          animate={{
            backgroundPosition: ['0px 0px', '60px 60px']
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear'
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Badge className="mb-4 text-sm px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
            {t('landing.productShowcase.badge')}
          </Badge>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
            {t('landing.productShowcase.heroTitleStart')}{' '}
            <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500 bg-clip-text text-transparent">
              {t('landing.productShowcase.heroTitleHighlight')}
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t('landing.productShowcase.heroSubtitle')}
          </p>
        </motion.div>

        {/* Main Demo Section */}
        <motion.div
          className="max-w-6xl mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative">
            {/* Rotating Rings */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              animate={{ rotate: 360 }}
              transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
            >
              <div className="w-[800px] h-[800px] rounded-full border-2 border-emerald-500/10" />
            </motion.div>
            <motion.div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              animate={{ rotate: -360 }}
              transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
            >
              <div className="w-[700px] h-[700px] rounded-full border border-teal-500/10" />
            </motion.div>

            {/* Central Demo Image/Video Container */}
            <div className="relative aspect-video max-w-5xl mx-auto">
              {/* Glow Effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-emerald-500/30 via-teal-500/30 to-blue-500/30 rounded-3xl blur-3xl"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5]
                }}
                transition={{ duration: 4, repeat: Infinity }}
              />

              {/* Main Demo Video Display */}
              <motion.div
                className="relative z-10 rounded-3xl overflow-hidden border-4 border-emerald-500/30 shadow-2xl bg-gray-900"
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                whileHover={{ scale: 1.02, borderColor: 'rgba(16, 185, 129, 0.6)' }}
              >
                {/* Demo Video */}
                <video
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{
                    filter: 'drop-shadow(0 10px 40px rgba(16, 185, 129, 0.4))'
                  }}
                >
                  <source src="https://cdn.studyield.com/videos/onboarding/1775121437497-tufrr.mp4" type="video/mp4" />
                  {/* Fallback image if video doesn't load */}
                  <img
                    src="/openclaw-logo.avif"
                    alt="OpenClaw Configuration Demo"
                    className="w-full h-full object-cover"
                  />
                </video>

                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent pointer-events-none" />

                {/* Floating particles */}
                {[...Array(8)].map((_, idx) => (
                  <motion.div
                    key={idx}
                    className="absolute w-2 h-2 bg-emerald-500 rounded-full pointer-events-none"
                    style={{
                      top: `${Math.random() * 100}%`,
                      left: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      y: [0, -30, 0],
                      opacity: [0.3, 1, 0.3],
                      scale: [1, 1.5, 1]
                    }}
                    transition={{
                      duration: 3 + idx * 0.5,
                      repeat: Infinity,
                      delay: idx * 0.3,
                      ease: 'easeInOut'
                    }}
                  />
                ))}
              </motion.div>

              {/* Floating AI Model Icons */}
              {floatingIcons.map(({ Icon, color, delay }, idx) => (
                <motion.div
                  key={idx}
                  className="absolute cursor-grab active:cursor-grabbing"
                  style={{
                    top: '50%',
                    left: '50%',
                    x: '-50%',
                    y: '-50%'
                  }}
                  drag
                  dragConstraints={{
                    top: -200,
                    left: -200,
                    right: 200,
                    bottom: 200,
                  }}
                  dragElastic={0.3}
                  whileHover={{ scale: 1.3, rotate: 15 }}
                  whileTap={{ scale: 0.9 }}
                  animate={{
                    x: [
                      '-50%',
                      `${Math.cos((idx * Math.PI) / 2) * 200 - 50}%`,
                      '-50%'
                    ],
                    y: [
                      '-50%',
                      `${Math.sin((idx * Math.PI) / 2) * 200 - 50}%`,
                      '-50%'
                    ],
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    delay: delay,
                    ease: 'easeInOut'
                  }}
                >
                  <div className="w-16 h-16 rounded-xl bg-background border-2 border-emerald-500/30 backdrop-blur-sm flex items-center justify-center shadow-lg hover:shadow-emerald-500/50 transition-shadow">
                    <Icon className={`w-8 h-8 ${color}`} />
                  </div>
                </motion.div>
              ))}

              {/* Connection Lines from Icons to Center */}
              <svg className="absolute inset-0 pointer-events-none" style={{ width: '100%', height: '100%' }}>
                {floatingIcons.map((_, idx) => {
                  const angle = (idx * 90);
                  const x2 = `${50 + Math.cos((angle * Math.PI) / 180) * 35}%`;
                  const y2 = `${50 + Math.sin((angle * Math.PI) / 180) * 35}%`;

                  return (
                    <motion.line
                      key={`line-${idx}`}
                      x1="50%"
                      y1="50%"
                      x2={x2}
                      y2={y2}
                      stroke="rgba(16, 185, 129, 0.2)"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                      animate={{
                        opacity: [0.2, 0.5, 0.2],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: idx * 0.3
                      }}
                    />
                  );
                })}
              </svg>
            </div>
          </div>
        </motion.div>

        {/* Comparison Timeline Below */}
        <div className="max-w-7xl mx-auto mt-20">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              {t('landing.productShowcase.title')} <span className="bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">{t('landing.productShowcase.titleHighlight')}</span>
            </h3>
          </motion.div>

          {/* VS Divider with OpenClaw icon */}
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 transform -translate-x-1/2 hidden lg:flex flex-col items-center justify-center z-20">
              <motion.div
                className="relative"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                {/* Glowing circle */}
                <motion.div
                  className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500 blur-xl"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                <motion.div
                  className="relative h-24 w-24 rounded-full bg-background border-4 border-emerald-500 flex items-center justify-center shadow-2xl p-4"
                  animate={{
                    rotate: [0, 360],
                  }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                >
                  <img
                    src="/openclaw.svg"
                    alt="OpenClaw"
                    className="w-full h-full object-contain"
                  />
                </motion.div>
                <motion.div
                  className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-red-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg whitespace-nowrap"
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  VS
                </motion.div>
              </motion.div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
              {/* OLD WAY - Pain */}
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <motion.div
                    className="h-16 w-16 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg"
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <X className="h-8 w-8 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="text-2xl font-bold text-red-600 dark:text-red-400">{t('landing.productShowcase.manualSetup.title')}</h3>
                    <p className="text-sm text-red-600/70 dark:text-red-400/70">{t('landing.productShowcase.manualSetup.subtitle')}</p>
                  </div>
                </div>

                {/* Timeline */}
                <div className="space-y-4 relative pl-8 border-l-2 border-red-300 dark:border-red-800">
                  {painPoints.map((point, i) => (
                    <motion.div
                      key={i}
                      className="relative"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.15 }}
                    >
                      {/* Timeline dot */}
                      <motion.div
                        className="absolute -left-[33px] top-1 h-4 w-4 rounded-full bg-red-500 border-4 border-background"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                      />

                      <div className="bg-red-50/50 dark:bg-red-950/20 p-4 rounded-lg border border-red-200/50 dark:border-red-800/30 backdrop-blur-sm">
                        <div className="flex items-start gap-3">
                          <motion.div
                            animate={{ rotate: [0, 90, 0] }}
                            transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                          >
                            <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                          </motion.div>
                          <span className="text-red-900 dark:text-red-200 text-sm font-medium">{point.pain}</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Time indicator */}
                <motion.div
                  className="mt-8 text-center p-5 bg-red-100/50 dark:bg-red-950/20 rounded-2xl border-2 border-red-300 dark:border-red-800"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                >
                  <motion.div
                    className="text-3xl font-black text-red-600 dark:text-red-400 mb-1"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {t('landing.productShowcase.manualSetup.time')}
                  </motion.div>
                  <div className="text-sm text-red-600/80 dark:text-red-400/80 font-medium">{t('landing.productShowcase.manualSetup.timeLabel')}</div>
                </motion.div>
              </motion.div>

              {/* NEW WAY - Solution */}
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {/* Recommended badge */}
                <motion.div
                  className="absolute -top-3 right-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg z-10"
                  animate={{
                    y: [0, -5, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                >
                  ✨ {t('landing.productShowcase.setOpenClaw.recommended')}
                </motion.div>

                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <motion.div
                    className="h-16 w-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg"
                    animate={{
                      boxShadow: [
                        '0 0 20px rgba(16, 185, 129, 0.3)',
                        '0 0 40px rgba(16, 185, 129, 0.6)',
                        '0 0 20px rgba(16, 185, 129, 0.3)',
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Check className="h-8 w-8 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">{t('landing.productShowcase.setOpenClaw.title')}</h3>
                    <p className="text-sm text-emerald-600/80 dark:text-emerald-400/80">{t('landing.productShowcase.setOpenClaw.subtitle')}</p>
                  </div>
                </div>

                {/* Timeline */}
                <div className="space-y-4 relative pl-8 border-l-2 border-emerald-300 dark:border-emerald-800">
                  {painPoints.map((point, i) => (
                    <motion.div
                      key={i}
                      className="relative"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.15 }}
                      whileHover={{ x: 4 }}
                    >
                      {/* Timeline dot */}
                      <motion.div
                        className="absolute -left-[33px] top-1 h-4 w-4 rounded-full bg-emerald-500 border-4 border-background"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                      />

                      <div className="bg-emerald-50/50 dark:bg-emerald-950/20 p-4 rounded-lg border border-emerald-200/50 dark:border-emerald-800/30 backdrop-blur-sm hover:border-emerald-400/50 transition-colors">
                        <div className="flex items-start gap-3">
                          <motion.div
                            animate={{
                              scale: [1, 1.2, 1],
                            }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                          >
                            <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          </motion.div>
                          <span className="text-emerald-900 dark:text-emerald-100 text-sm font-medium">{point.solution}</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Time indicator + CTA */}
                <motion.div
                  className="mt-8 text-center p-5 bg-gradient-to-br from-emerald-50/80 to-teal-50/80 dark:from-emerald-950/30 dark:to-teal-950/30 rounded-2xl border-2 border-emerald-300 dark:border-emerald-800 relative overflow-hidden"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                >
                  {/* Animated glow */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10"
                    animate={{
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                    }}
                  />

                  <div className="relative z-10">
                    <motion.div
                      className="text-3xl font-black bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent mb-1"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {t('landing.productShowcase.setOpenClaw.time')}
                    </motion.div>
                    <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mb-3">{t('landing.productShowcase.setOpenClaw.timeLabel')}</div>

                    <Link to="/register">
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button size="default" className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-500/30">
                          {t('landing.productShowcase.setOpenClaw.cta')}
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </motion.div>
                    </Link>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
